import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
} from "@mui/material";
import { Close, Download, Draw } from "@mui/icons-material";
import { downloadPDF } from "@/fluxvet/services/documentGenerator";
import { toast } from "react-toastify";
import { useFluxSigner } from "@/hooks/fluxsigner/useFluxSigner";
import { useFluxSignerContext } from "@/contexts/FluxSignerContext";
import { Link } from "react-router-dom";

// =============================================================================
// INTERFACES
// =============================================================================

interface DocumentPreviewModalProps {
  // Controle de abertura/fechamento
  open: boolean;
  onClose: () => void;

  // Dados do documento
  documentBase64: string | null;
  documentTitle?: string;
  documentFilename?: string;
  documentUUID?: string;

  // Estado de carregamento
  loading?: boolean;

  // Callback para assinar documento (recebe provider, password, lifeTime e certificateAlias)
  onSign?: (
    provider: "vidaas" | "safeid" | "fluxsigner",
    password?: string,
    lifeTime?: number,
    certificateAlias?: string
  ) => Promise<void> | void;
}

// Lista de provedores de assinatura
const ProviderOptions = [
  { label: "VidaaS", value: "vidaas" },
  { label: "SafeID", value: "safeid" },
  {
    label: "FluxSigner - Assinatura digital do computador",
    value: "fluxsigner",
  },
];

// Opções de tempo de sessão
const LifeTimeOptions = [
  { label: "Assinatura única", value: 0 },
  { label: "1 hora", value: 3600 },
  { label: "4 horas", value: 14400 },
  { label: "8 horas", value: 28800 },
  { label: "12 horas", value: 43200 },
];

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Modal para visualização de documentos PDF
 * Exibe o documento em um iframe e oferece opções de download e assinatura digital
 * Inclui seleção de provedor de assinatura e configurações
 */
export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  onClose,
  documentBase64,
  documentTitle = "Visualização do Documento",
  documentFilename = "documento",
  documentUUID,
  loading = false,
  onSign,
}) => {
  // URL do blob para o iframe
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Estado de assinatura (apenas para loading)
  const [signing, setSigning] = useState(false);

  // Estados para configuração de assinatura (sempre visível)
  const [selectedProvider, setSelectedProvider] = useState<
    "vidaas" | "safeid" | "fluxsigner"
  >("vidaas");
  const [password, setPassword] = useState("");
  const [lifeTime, setLifeTime] = useState(0);
  const [selectedCertificate, setSelectedCertificate] = useState("");

  // Hook do FluxSigner para gerenciar certificados digitais
  const {
    isAvailable: fluxSignerAvailable,
    certificates,
    certificatesLoading,
    loadCertificates,
    forceCheckExtension,
  } = useFluxSigner();

  // Contexto do FluxSigner para verificar se a extensão está ativada
  const { contractFluxSigner } = useFluxSignerContext();

  /**
   * Reseta o estado de signing e configurações quando o modal fecha
   */
  useEffect(() => {
    if (!open) {
      setSigning(false);
      setPassword("");
      setSelectedCertificate("");
    }
  }, [open]);

  /**
   * Verifica a extensão Chrome e carrega certificados quando necessário
   */
  useEffect(() => {
    if (open) {
      forceCheckExtension();
      if (
        selectedProvider === "fluxsigner" &&
        fluxSignerAvailable &&
        contractFluxSigner
      ) {
        loadCertificates();
      }
    }
  }, [
    open,
    selectedProvider,
    fluxSignerAvailable,
    contractFluxSigner,
    forceCheckExtension,
    loadCertificates,
  ]);

  /**
   * Converte base64 para URL do blob quando o documento é carregado
   */
  useEffect(() => {
    if (documentBase64) {
      try {
        // Converte base64 para blob
        const byteCharacters = atob(documentBase64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Cria URL do blob
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);

        // Cleanup: revoga a URL quando o componente é desmontado
        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error("Erro ao processar documento:", error);
      }
    }
  }, [documentBase64]);

  /**
   * Faz download do documento
   */
  const handleDownload = () => {
    if (documentBase64) {
      downloadPDF(documentBase64, documentFilename);
    }
  };

  /**
   * Executa a assinatura do documento
   */
  const handleSignDocument = async () => {
    if (!onSign) return;

    // Validações
    if (selectedProvider === "safeid" && !password) {
      toast.warning("Por favor, informe a senha do SafeID.");
      return;
    }

    if (selectedProvider === "fluxsigner" && !selectedCertificate) {
      toast.warning("Por favor, selecione um certificado digital.");
      return;
    }

    if (selectedProvider === "fluxsigner" && !contractFluxSigner) {
      toast.warning("A extensão FluxSigner não está ativada.");
      return;
    }

    setSigning(true);
    try {
      // Chama o callback de assinatura com os parâmetros configurados
      await onSign(selectedProvider, password, lifeTime, selectedCertificate);

      // Fecha o modal após assinar com sucesso
      onClose();
    } catch (error) {
      console.error("Erro ao assinar documento:", error);
      toast.error(
        "Não foi possível assinar o documento. Por favor, tente novamente."
      );
    } finally {
      setSigning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "900px",
        },
      }}
    >
      {/* ========== CABEÇALHO ========== */}
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight={600}>
            {documentTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* ========== CONTEÚDO ========== */}
      <DialogContent dividers sx={{ p: 0, display: "flex", height: "100%" }}>
        {loading ? (
          // Estado de carregamento
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <CircularProgress size={48} sx={{ color: "#00A884" }} />
            <Typography variant="body2" color="text.secondary">
              Gerando documento...
            </Typography>
          </Box>
        ) : blobUrl ? (
          // Layout: PDF à esquerda, Configurações à direita
          <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
            {/* Visualização do PDF */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <iframe
                src={blobUrl}
                title={documentTitle}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </Box>

            {/* Configurações de Assinatura */}
            {onSign && (
              <Box
                sx={{
                  width: 380,
                  borderLeft: "1px solid #e0e0e0",
                  bgcolor: "#fafafa",
                  p: 3,
                  overflowY: "auto",
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Assinatura Digital
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure as opções de assinatura eletrônica
                    </Typography>
                  </Box>

                  {/* Seleção de Provedor */}
                  <FormControl fullWidth size="small">
                    <InputLabel id="provider-label">
                      Provedor de Assinatura
                    </InputLabel>
                    <Select
                      labelId="provider-label"
                      label="Provedor de Assinatura"
                      value={selectedProvider}
                      onChange={(e) =>
                        setSelectedProvider(
                          e.target.value as "vidaas" | "safeid" | "fluxsigner"
                        )
                      }
                    >
                      {ProviderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Senha para SafeID */}
                  {selectedProvider === "safeid" && (
                    <TextField
                      fullWidth
                      size="small"
                      type="password"
                      label="Senha do SafeID"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  )}

                  {/* Certificado para FluxSigner */}
                  {selectedProvider === "fluxsigner" && (
                    <Stack spacing={2}>
                      {!contractFluxSigner ? (
                        <Alert severity="warning" sx={{ fontSize: "0.875rem" }}>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Extensão não ativada
                          </Typography>
                          <Typography variant="caption" display="block" mb={1}>
                            Para usar a assinatura digital do computador, ative
                            a extensão FluxSigner.
                          </Typography>
                          <Link
                            to="/extensions"
                            style={{
                              color: "#1976d2",
                              textDecoration: "underline",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                            onClick={onClose}
                          >
                            Ativar extensão FluxSigner
                          </Link>
                        </Alert>
                      ) : certificatesLoading ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          p={2}
                        >
                          <CircularProgress size={20} />
                          <Typography variant="body2" ml={1}>
                            Carregando certificados...
                          </Typography>
                        </Stack>
                      ) : certificates.length === 0 ? (
                        <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                          <Typography variant="caption">
                            Nenhum certificado digital encontrado. Verifique se
                            você tem certificados ICP-Brasil instalados.
                          </Typography>
                        </Alert>
                      ) : (
                        <FormControl fullWidth size="small">
                          <InputLabel id="certificate-label">
                            Certificado Digital
                          </InputLabel>
                          <Select
                            labelId="certificate-label"
                            label="Certificado Digital"
                            value={selectedCertificate}
                            onChange={(e) =>
                              setSelectedCertificate(e.target.value)
                            }
                          >
                            {certificates.map((cert) => (
                              <MenuItem key={cert.alias} value={cert.alias}>
                                <Stack>
                                  <Typography variant="body2" fontWeight="bold">
                                    {cert.commonName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {cert.providerName}
                                  </Typography>
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                  )}

                  {/* Tempo de Sessão (não aplicável para FluxSigner) */}
                  {selectedProvider !== "fluxsigner" && (
                    <FormControl fullWidth size="small">
                      <InputLabel id="lifetime-label">
                        Tempo de Sessão
                      </InputLabel>
                      <Select
                        labelId="lifetime-label"
                        label="Tempo de Sessão"
                        value={lifeTime}
                        onChange={(e) => setLifeTime(Number(e.target.value))}
                      >
                        {LifeTimeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Botão de Download */}
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Download />}
                    onClick={handleDownload}
                    disabled={!documentBase64 || loading || signing}
                    sx={{
                      textTransform: "none",
                      borderColor: "#00A884",
                      color: "#00A884",
                      fontSize: "0.95rem",
                      py: 1.5,
                      "&:hover": {
                        borderColor: "#008c6e",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Baixar PDF
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          // Estado vazio
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Nenhum documento para exibir
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tente gerar o documento novamente
            </Typography>
          </Box>
        )}
      </DialogContent>

      {/* ========== AÇÕES ========== */}
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        {/* Botão Fechar */}
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={signing}
          sx={{
            textTransform: "none",
            fontSize: "0.95rem",
            px: 3,
          }}
        >
          Fechar
        </Button>

        {/* Botão Assinar (apenas se onSign estiver definido) */}
        {onSign && (
          <Button
            variant="contained"
            startIcon={
              signing ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <Draw />
              )
            }
            onClick={handleSignDocument}
            disabled={signing || !documentBase64 || loading}
            sx={{
              bgcolor: "#00A884",
              "&:hover": { bgcolor: "#008c6e" },
              textTransform: "none",
              fontSize: "0.95rem",
              px: 4,
              fontWeight: 600,
              boxShadow: 2,
            }}
          >
            {signing ? "Assinando..." : "Assinar Documento"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewModal;
